import { useState } from "react"

import { format } from "date-fns"
import { Search, Truck, X } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useAuth } from "@/contexts/AuthContext"
import { useDuplicateRouteInstance } from "@/hooks/useCollections"
import { formatDayMonthYear } from "@/lib/dateFormats"
import { cn } from "@/lib/utils"
import type { Route } from "@/types/collections"

import AdhocCodeField from "../AdhocCodeField"

interface DuplicateRouteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  routes: Route[]
  selectedDate: Date
  onDuplicated: (newRouteId: string) => void
}

/** Same operation as the details panel; the sequence lives in useDuplicateRouteInstance. */
export default function DuplicateRouteDialog({
  open,
  onOpenChange,
  routes,
  selectedDate,
  onDuplicated,
}: DuplicateRouteDialogProps) {
  const { user } = useAuth()
  const duplicateRouteInstance = useDuplicateRouteInstance()

  const duplicate = async (source: Route, reason: string, customCode: string) => {
    try {
      const result = await duplicateRouteInstance.mutateAsync({
        source,
        dateStr: format(selectedDate, "yyyy-MM-dd"),
        reason,
        authorName: user?.full_name || "Unknown User",
        customCode,
      })
      onDuplicated(result.id)
      onOpenChange(false)
      toast.success("Route duplicated", {
        description: `Ad-hoc route created from ${result.sourceCode} with ${result.stopCount} ${
          result.stopCount === 1 ? "stop" : "stops"
        }.`,
      })
      return true
    } catch (error) {
      console.error("Error duplicating route:", error)
      toast.error("Could not duplicate route", {
        description: "No route was created. Please try again.",
      })
      return false
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DuplicateRouteForm
          routes={routes}
          selectedDate={selectedDate}
          onCancel={() => onOpenChange(false)}
          onDuplicate={duplicate}
        />
      </DialogContent>
    </Dialog>
  )
}

const matchesRouteSearch = (route: Route, term: string) => {
  if (!term.trim()) return true
  const q = term.toLowerCase()
  return (
    route.code.toLowerCase().includes(q) ||
    route.driverName.toLowerCase().includes(q) ||
    route.vehicleReg?.toLowerCase().includes(q) ||
    format(route.plannedStartTime, "HH:mm").includes(term) ||
    format(route.plannedEndTime, "HH:mm").includes(term)
  )
}

/** Mounted only while open, so every opening starts with nothing chosen. */
function DuplicateRouteForm({
  routes,
  selectedDate,
  onCancel,
  onDuplicate,
}: {
  routes: Route[]
  selectedDate: Date
  onCancel: () => void
  onDuplicate: (source: Route, reason: string, customCode: string) => Promise<boolean>
}) {
  const [routeToDuplicate, setRouteToDuplicate] = useState<Route | null>(null)
  const [notes, setNotes] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  // Optional: blank means "generate one".
  const [code, setCode] = useState("")
  const [isDuplicating, setIsDuplicating] = useState(false)

  const confirm = async () => {
    if (!routeToDuplicate || !notes.trim()) return
    setIsDuplicating(true)
    try {
      if (await onDuplicate(routeToDuplicate, notes, code)) {
        setRouteToDuplicate(null)
        setNotes("")
        setCode("")
        setSearchTerm("")
      }
    } finally {
      setIsDuplicating(false)
    }
  }

  const matchingRoutes = routes.filter((route) => matchesRouteSearch(route, searchTerm))

  return (
    <>
      <DialogHeader>
        <DialogTitle>Duplicate route as ad-hoc</DialogTitle>
        <DialogDescription>
          Copy an existing route and its stops as a new ad-hoc route for{" "}
          {formatDayMonthYear(selectedDate)}.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="grid gap-2">
          <Label>Route to duplicate *</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-palette-jungleGreen400" />
            <Input
              placeholder="Search by route code, driver, or time..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-9 border-palette-jungleGreen200 bg-white pl-9 text-sm"
            />
          </div>
          {routeToDuplicate && (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2">
              <Truck size={14} className="text-amber-600" />
              <span className="text-sm font-medium">{routeToDuplicate.code}</span>
              <span className="text-xs text-palette-jungleGreen500">
                {routeToDuplicate.driverName}
              </span>
              <span className="text-xs text-palette-jungleGreen500">
                {format(routeToDuplicate.plannedStartTime, "HH:mm")}-
                {format(routeToDuplicate.plannedEndTime, "HH:mm")}
              </span>
              <button
                type="button"
                onClick={() => setRouteToDuplicate(null)}
                className="ml-auto text-palette-jungleGreen400 hover:text-palette-jungleGreen600"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <div className="max-h-[180px] overflow-y-auto rounded-md border bg-white">
            {matchingRoutes.length === 0 ? (
              <div className="p-4 text-center text-sm text-palette-jungleGreen500">
                {searchTerm ? "No routes match your search" : "No routes available"}
              </div>
            ) : (
              <div className="divide-y">
                {matchingRoutes.map((route) => {
                  const isSelected = routeToDuplicate?.id === route.id
                  return (
                    <div
                      key={route.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setRouteToDuplicate(route)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          setRouteToDuplicate(route)
                        }
                      }}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-palette-jungleGreen000",
                        isSelected && "bg-amber-50"
                      )}
                    >
                      <Truck
                        size={14}
                        className={cn(
                          "flex-shrink-0 text-palette-jungleGreen400",
                          isSelected && "text-amber-600"
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{route.code}</span>
                          {isSelected && (
                            <span className="rounded bg-amber-200 px-1.5 py-0.5 text-xs text-amber-800">
                              Selected
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-palette-jungleGreen500">
                          <span>{route.driverName}</span>
                          <span className="text-palette-jungleGreen300">•</span>
                          <span>
                            {format(route.plannedStartTime, "HH:mm")}-
                            {format(route.plannedEndTime, "HH:mm")}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <AdhocCodeField id="duplicate-adhoc-code" value={code} onChange={setCode} />
        <div className="grid gap-2">
          <Label htmlFor="duplicate-notes">Reason for duplicating *</Label>
          <Textarea
            id="duplicate-notes"
            placeholder="e.g. Same run needed for different day / extra capacity"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[80px] resize-none bg-white"
            maxLength={500}
          />
          <p className="text-xs text-palette-jungleGreen500">
            This will be saved as a route comment for audit.
          </p>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={confirm}
          disabled={!routeToDuplicate || !notes.trim() || isDuplicating}
          variant="confirm"
        >
          {isDuplicating ? "Duplicating…" : "Duplicate as ad-hoc route"}
        </Button>
      </DialogFooter>
    </>
  )
}
